import { defineStore } from 'pinia'
import { logError } from '~/utils/log-error'
import type {
  AreaOfInterest,
  EditableField,
  UserAccountInfo,
  UserAccountState,
  UserContact,
  UserNewsletters,
} from '~/models/userAccount'

export const useUserAccountStore = defineStore('user-account-store', {
  state: (): UserAccountState => ({
    loading: false,
    lastFetched: null,
    contactName: '',
    createdAt: null,
    newsletters: null,
    areasOfInterest: [],
    address: null,
    accountManager: null,
    fields: {
      primaryEmail: {
        value: '',
        canEdit: true,
        updated: false,
        isEditable: false,
        isSubmitting: false,
      },
      officeNumber: {
        value: '',
        canEdit: true,
        updated: false,
        isEditable: false,
        isSubmitting: false,
      },
      mobileNumber: {
        value: '',
        canEdit: true,
        updated: false,
        isEditable: false,
        isSubmitting: false,
      },
    },
  }),

  actions: {
    clear() {
      this.loading = false
      this.lastFetched = null
      this.contactName = ''
      this.createdAt = null
      this.newsletters = null
      this.areasOfInterest = []
      this.address = null
      this.accountManager = null
      this.fields = {
        primaryEmail: {
          value: '',
          canEdit: true,
          updated: false,
          isEditable: false,
          isSubmitting: false,
        },
        officeNumber: {
          value: '',
          canEdit: true,
          updated: false,
          isEditable: false,
          isSubmitting: false,
        },
        mobileNumber: {
          value: '',
          canEdit: true,
          updated: false,
          isEditable: false,
          isSubmitting: false,
        },
      }
    },

    async fetchUserAccountInfo(forceRefresh = false) {
      const { $restClient } = useNuxtApp()

      if (
        !forceRefresh &&
        this.lastFetched &&
        Date.now() - this.lastFetched < 5 * 60 * 1000
      ) {
        return
      }

      this.loading = true

      try {
        const response =
          (await $restClient.getUserAccountInfo()) as UserAccountInfo
        delete response.newsletters.policyNewsletter
        this.newsletters = response.newsletters
        this.areasOfInterest = response.areasOfInterest
          ?.filter((area) => area.hidden !== true)
          ?.toSorted(
            (before, after) => before.displayOrder - after.displayOrder,
          )
        this.contactName = `${response.contact.firstName} ${response.contact.lastName}`
        this.createdAt = response.contact.createdAt
        this.fields.primaryEmail.value = response.contact.primaryEmail
        this.fields.officeNumber.value = response.contact.phone
        this.fields.mobileNumber.value = response.contact.mobile
        if (response.address.main) {
          this.address = response.address
        }
        if (response.accountManager) {
          this.accountManager = response.accountManager
        }

        this.lastFetched = Date.now()
      } catch (error) {
        logError(error)
      } finally {
        this.loading = false
      }
    },

    updateField({
      key,
      values,
    }: {
      key: string
      values: Partial<EditableField>
    }) {
      const field = this.fields[key as keyof typeof this.fields]

      // Store the original value when entering edit mode
      if (values.isEditable && !field.isEditable) {
        this.fields[key as keyof typeof this.fields] = {
          ...field,
          originalValue: field.value,
          ...values,
        }
      } else {
        this.fields[key as keyof typeof this.fields] = {
          ...field,
          ...values,
        }
      }
    },

    cancelEdit(key: string) {
      const field = this.fields[key as keyof typeof this.fields]

      if (
        (key === 'officeNumber' || key === 'mobileNumber') &&
        field.originalValue === ''
      ) {
        this.fields[key as keyof typeof this.fields] = {
          ...field,
          value: field.originalValue,
          isEditable: false,
          isSubmitting: false,
          updated: false,
        }
        return
      }

      if (field.originalValue !== undefined) {
        this.fields[key as keyof typeof this.fields] = {
          ...field,
          value: field.originalValue,
          isEditable: false,
          isSubmitting: false,
          updated: false,
        }
      } else {
        this.fields[key as keyof typeof this.fields] = {
          ...field,
          isEditable: false,
          isSubmitting: false,
          updated: false,
        }
      }
    },

    async updateSubscriptions(newNewsletters: UserNewsletters) {
      const { $restClient } = useNuxtApp()

      try {
        await $restClient.setUserAccountInfo({ newsletters: newNewsletters })

        this.newsletters = newNewsletters
      } catch (e) {
        logError(e)
      }
    },

    async updateAreaOfInterests(interests: string[]) {
      const { $restClient } = useNuxtApp()

      try {
        const params = <Partial<UserAccountInfo>>{
          areasOfInterest: interests as Partial<AreaOfInterest>[],
        }
        await $restClient.setUserAccountInfo(params)

        this.areasOfInterest = this.areasOfInterest.map(
          (area: AreaOfInterest) => ({
            ...area,
            selected: interests.includes(area.value),
          }),
        )
      } catch (e) {
        logError(e)
      }
    },

    async submitFieldUpdate({
      key,
      values,
    }: {
      key: string
      values: Partial<EditableField>
    }) {
      const fieldKey = key as keyof typeof this.fields
      const field = this.fields[fieldKey]
      field.isSubmitting = true

      try {
        const { $restClient } = useNuxtApp()

        const updatePayload = this.createContactUpdatePayload(key, values.value)

        if (updatePayload) {
          await $restClient.setUserAccountInfo(updatePayload)
        }

        this.updateField({ key, values })
        field.isSubmitting = false

        this.updateField({
          key,
          values: {
            updated: true,
          },
        })

        setTimeout(() => {
          this.updateField({
            key,
            values: {
              isEditable: false,
              isSubmitting: false,
              updated: false,
            },
          })
        }, 5000)
      } catch (error) {
        logError(error)
      } finally {
        field.isSubmitting = false
      }
    },

    createContactUpdatePayload(
      key: string,
      value: string,
    ): UserAccountInfo | null {
      const contactFieldMap: Record<string, keyof UserContact> = {
        primaryEmail: 'primaryEmail',
        officeNumber: 'phone',
        mobileNumber: 'mobile',
      }

      const contactField = contactFieldMap[key]

      if (!contactField) {
        return null
      }

      return {
        contact: {
          [contactField]: value,
        } as Partial<UserContact>,
      } as UserAccountInfo
    },
  },
})
